// Import global utils
import { openWindow } from '@automator/shared/utils/open-window';
import { getURLParameter } from '@automator/shared/utils/get-url-parameter';
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x, sprintf } from '@wordpress/i18n';

// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

import {
	sortArrayOfObjectsAlphabetically,
	getDOMElements,
	filterDeprecatedItems
} from '../common/utilities.js';

import { getIntegrationIconURL } from '@automator/builder/_legacy/js/integrations/utilities';
import { isThirdParty } from '@automator/builder/definitions/integrations/_filters';


import Modal                   from '../common/modal.js';

import ItemHeaderMenu          from './action-menu.js';
import ItemCreatorTaskSelector from './items-list.js';

class ItemCreatorIntegrations {
	constructor( itemType, $currentItem, attributes = {} ){
		this.itemType     = itemType;
		this.$currentItem = $currentItem;
		this.attributes   = attributes;
		this.integrations = [];

		// Render DOM elements
		this.render();

		// Handle URL parameters to continue process of connecting an integration
		this.handleContinueIntegrationConnection();
	}

	render(){
		// Get content
		const content = {
			// The action menu. The three dots on the upper right corner.
			$actionMenu: new ItemHeaderMenu( this.itemType )
		}

		// Create content
		const $template = document.createElement( 'div' );
		$template.classList.add( 'uap-item' );

		// Save item as a property
		this.$item = $template;

		// Add content
		$template.insertAdjacentHTML( 'beforeEnd', `
			<div class="uap-item-inner">
				<div class="uap-item-sidebar">
					<div class="uap-item-icon"></div>
				</div>
				<div class="uap-item-top-header">
					<div class="uap-item-top-header-left">
						${ escapeHTML( __( 'Select an integration', 'uncanny-automator' ) ) }
					</div>
					<div class="uap-item-top-header-right"></div>
				</div>
				<div class="uap-item-inner-content"></div>
			</div>
		` );

		// Add action menu
		$template.querySelector( '.uap-item-top-header-right' )
			.appendChild( content.$actionMenu.get(0) );

		// Add integrations grid
		$template.querySelector( '.uap-item-inner-content' )
			.appendChild( this.createIntegrations().get(0) );

		// Append footer. Usually this has the "Discover more integrations"
		$template.querySelector( '.uap-item-inner-content' )
			.appendChild( this.createFooter() );

		/**
		 * Add the element to the DOM
		 * Check if the item already exists in the DOM. If that's the case, we will _replace_ the node
		 */
		if ( isDefined( this.$currentItem ) ){
			// Empty container
			this.$currentItem.replaceWith( $template );
		} else {
			// Check if it's inside a conditions block
			if ( ! isEmpty( this.attributes.conditionsGroupID ) && isDefined( UncannyAutomator.recipe.conditions[ this.attributes.conditionsGroupID ] ) ) {
				// Get the conditions block
				const $conditionsBlock = UncannyAutomator.recipe.conditions[ this.attributes.conditionsGroupID ].$currentConditionsBlock;
				
				if ( $conditionsBlock ) {
					// Add it at the bottom of the actions wrapper
					$conditionsBlock.$actionsWrapper.append( $template );
				}
			} else {
				// Get last item and append next to it
				const $lastItem = getDOMElements( 'lastItem', {
					type: this.itemType
				}).get(0);

				if ( this.itemType == 'trigger' ) {
					// Check if there is a last item
					if ( isDefined( $lastItem ) ) {
						// Insert after the last item
						$lastItem.insertAdjacentElement( 'afterEnd', $template );
					}
					else {
						// Otherwise append the item to the metabox
						let $metabox = getDOMElements( 'metabox', {
							type: this.itemType
						}).get(0);

						$metabox.appendChild( $template );
					}
				}
				
				if ( this.itemType == 'action' ) {
					// Check if there is at least one item
					let items = getDOMElements( 'items', {
						type: this.itemType
					});

					// Get the metabox
					let $metabox = getDOMElements( 'metabox', {
						type: this.itemType
					}).get(0);

					if ( items.length > 0 ){
						// Insert before the "Add action" button
						$metabox.querySelector( '.add-action-btn' )
							.insertAdjacentElement( 'beforeBegin', $template );
					} else {
						const $initialAction = $metabox.querySelector( '.uap-item--actions-initial' );

						if ( $initialAction ) {
							// Remove the initial "Add action" button
							$metabox.querySelector( '.uap-item--actions-initial' )
								.replaceWith( $template );
						} else {
							// If it doesn't have the initial action, add it above the "Add new action"
							$metabox.querySelector( '.metabox__footer.add-action-btn' )
								.insertAdjacentElement( 'beforeBegin', $template );
						}
					}
				}
			}
		}
	}

	createIntegrations(){
		let container    = $( '<div/>', { 'class': 'item-integrations' });

		// Get non empty integrations
		this.filterByNonEmptyIntegrations( this.itemType );

		// Order integrations
		this.sortIntegrations(); 

		// Iterate integrations
		this.integrations.forEach(( integration ) => {
			// Get integration ID
			const integrationID = integration.id;

			// Create integration element
			let integrationElement = this.createIntegration( integrationID, integration );

			// Create default action on click
			let onClick = () => {
				new ItemCreatorTaskSelector( this.itemType, integrationID, this.$item, this.attributes );
			}

			// Before what's going to happen on click
			// Check if it's an API integration
			if ( isDefined( integration.connected ) ){
				// Get current step and change the callback accordingly
				switch ( this.integrationModalCurrentStep( integration, integrationID ) ){
					// Step 1
					case 1:
						onClick = () => {
							// Set the URL parameters
							const urlParameters = {
								ua_connecting_integration: 1,
								ua_connecting_integration_id: integrationID,
								ua_connecting_integration_context: this.itemType
							}

							// Check if it's inside a conditions block
							if ( isDefined( this.attributes.conditionsGroupID ) ) {
								urlParameters.ua_connecting_integration_condition = this.attributes.conditionsGroupID;
							}

							// Set parameter to show the modal again in case the user reloads the page
							addParametersToURL( window.location.href, urlParameters, {
								setAsURL: true,
								refresh: false
							});

							// Show the modal to connect the integration
							this.connectIntegrationModal( integrationID, integration, {
								currentStep: 1
							});
						}

						break;

					// Step 2
					case 2:
						// -> Send to the settings page of the integration
						onClick = () => {
							// Set the URL parameters
							const urlParameters = {
								ua_connecting_integration: 1,
								ua_connecting_integration_id: integrationID,
								ua_connecting_integration_context: this.itemType
							}

							// Check if it's inside a conditions block
							if ( isDefined( this.attributes.conditionsGroupID ) ) {
								urlParameters.ua_connecting_integration_condition = this.attributes.conditionsGroupID;
							}

							// Set parameter to show the modal again in case the user reloads the page
							addParametersToURL( window.location.href, urlParameters, {
								setAsURL: true,
								refresh: false
							});

							// Show the modal to connect the integration
							this.connectIntegrationModal( integrationID, integration, {
								currentStep: 2
							});
						}

						break;

					// Step 3
					case 3:
						// Do nothing, leave the original callback,
						// which will just show the list of actions

						break;
				}
			}

			// Bind
			integrationElement.on( 'click', () => {
				onClick();

				// Send custom event to the document
				document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/integration-selected', {
					detail: {
						itemType: this.itemType,
						integrationCode: integrationID
					}
				} ) );
			} );

			// Append
			container.append( integrationElement );
		});

		// Return jQuery element
		return container;
	}

	connectIntegrationModal( integrationID, integration, options = {} ){
		// Extend the options
		options = Object.assign({
			currentStep: 1
		}, options );

		// Connect API url
		const connectApiUrl = addParametersToURL( UncannyAutomator._site.links.wp_admin, {
			recipe_ui_connect_automator_license: 'yes',
			return_to: encodeURIComponent( window.location.href ),
			origin: 'recipe_builder'
		});

		// Check what should be the first step
		let modalFirstStep = '';
		const isThirdPartyIntegration = isThirdParty( integrationID );
		const currentStep = isThirdPartyIntegration ? 0 : options.currentStep - 1;
		const completionSteps = isThirdPartyIntegration ? 1 : 2;

		// For third-party integrations, skip the first step entirely
		if ( ! isThirdPartyIntegration ) {
			// Check if wp is ready
			if ( UncannyAutomatorBackend._site.automator.is_pro_active ){
				modalFirstStep = `
					<div class="connect-integration-modal-step ${ options.currentStep > 1 ? 'connect-integration-modal-step--completed' : '' }">
						<div class="connect-integration-modal-step__icon">
							<uo-icon id="check-circle"></uo-icon>
						</div> 
						<div class="connect-integration-modal-step__description">
							<div class="connect-integration-modal-step__description-main">
								<strong>1.</strong> ${ 
									sprintf( 
										/* translators: 1. Trademarked term */
										escapeHTML( __( 'Activate your %1$s license key!', 'uncanny-automator' ) ), 
										'Uncanny Automator Pro'
									)
								}
							</div>
						</div>
						<div class="connect-integration-modal-step__action">
							<a id="connect-integration-modal-activate-license" class="connect-integration-modal-step__action-anchor">
								${ escapeHTML( __( 'Activate', 'uncanny-automator' ) ) } <uo-icon id="arrow-circle-right"></uo-icon>
							</a>
						</div>
					</div>
				`;
			}
			else {
				modalFirstStep = `
					<div class="connect-integration-modal-step ${ options.currentStep > 1 ? 'connect-integration-modal-step--completed' : '' }">
						<div class="connect-integration-modal-step__icon">
							<uo-icon id="face-smile-plus"></uo-icon>
						</div> 
						<div class="connect-integration-modal-step__description">
							<div class="connect-integration-modal-step__description-main">
								<strong>1.</strong> ${ 
									sprintf( 
										/* translators: 1. Trademarked term */
										escapeHTML( __( 'Sign up for a free %1$s account!', 'uncanny-automator' ) ), 
										'Uncanny Automator'
									)
								}
							</div>
						</div>
						<div class="connect-integration-modal-step__action">
							<a id="connect-integration-modal-sign-up" class="connect-integration-modal-step__action-anchor">
								${ escapeHTML( __( 'Sign up', 'uncanny-automator' ) ) } <uo-icon id="arrow-circle-right"></uo-icon>
							</a>
						</div>
					</div>
				`;
			}
		}

		// Create modal
		const modal = new Modal({
			title:   `${ escapeHTML( __( 'Connect integration', 'uncanny-automator' ) ) }`,
			content: `
				<div class="connect-integration-modal">
					<div class="connect-integration-modal-steps__counter">
						${ 
							sprintf( 
								/* translators: 1. Number, 2. Number */
								escapeHTML( __( '%1$s of %2$s steps completed', 'uncanny-automator' ) ), 
								currentStep,
								completionSteps
							) 
						}
					</div>
					<div class="connect-integration-modal-steps">
						<div class="connect-integration-modal-steps__list">
							${ modalFirstStep }
							${ ! isThirdPartyIntegration ? `
								<div class="connect-integration-modal-step ${ options.currentStep > 2 ? 'connect-integration-modal-step--completed' : '' } ${ options.currentStep == 1 ? 'connect-integration-modal-step--previous-not-completed' : '' }">
									<div class="connect-integration-modal-step__icon">
										<uo-icon id="plug"></uo-icon>
									</div> 
									<div class="connect-integration-modal-step__description">
										<div class="connect-integration-modal-step__description-main">
											<strong>2.</strong> ${
												sprintf(
													/* translators: 1. Trademarked term, 2. Trademarked term */
													escapeHTML( __( 'Connect your %1$s account to %2$s.', 'uncanny-automator' ) ),
													`<span class="connect-integration-modal-step-integration-name">
														<img src="${ getIntegrationIconURL( { integrationCode: integrationID } ) }">
														${ integration.name }
													</span>`, 
													'Uncanny Automator'
												)
											}
										</div>
									</div>
									<div class="connect-integration-modal-step__action">
										<span ${ options.currentStep == 1 ? 'uap-tooltip="' + escapeHTML( __( 'Please, complete the previous step first.', 'uncanny-automator' ) ) + '"' : '' }>
											<a id="connect-integration-modal-connect-account" class="connect-integration-modal-step__action-anchor">
												${ escapeHTML( __( 'Connect account', 'uncanny-automator' ) ) } <uo-icon id="arrow-circle-right"></uo-icon>
											</a>
										</span>
									</div>
								</div>
							` : `
								<div class="connect-integration-modal-step">
									<div class="connect-integration-modal-step__icon">
										<uo-icon id="plug"></uo-icon>
									</div> 
									<div class="connect-integration-modal-step__description">
										<div class="connect-integration-modal-step__description-main">
											<strong>1.</strong> ${
												sprintf(
													/* translators: 1. Trademarked term, 2. Trademarked term */
													escapeHTML( __( 'Connect your %1$s account to %2$s.', 'uncanny-automator' ) ),
													`<span class="connect-integration-modal-step-integration-name">
														<img src="${ getIntegrationIconURL( { integrationCode: integrationID } ) }">
														${ integration.name }
													</span>`, 
													'Uncanny Automator'
												)
											}
										</div>
									</div>
									<div class="connect-integration-modal-step__action">
										<a id="connect-integration-modal-connect-account" class="connect-integration-modal-step__action-anchor">
											${ escapeHTML( __( 'Connect account', 'uncanny-automator' ) ) } <uo-icon id="arrow-circle-right"></uo-icon>
										</a>
									</div>
								</div>
							` }
						</div>
					</div>
				</div>
			`,
			buttons: {
				cancel:  escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ),
				confirm: null
			}
		}, {
			size: 'extra-large'
		});

		modal.setEvents({
			onCancel: () => {
				// Close and destroy modal
				modal.destroy();

				// Remove the parameters that force the modal to be visible on load
				setTimeout(() => {
					addParametersToURL( window.location.href, {
						ua_connecting_integration:    '',
						ua_connecting_integration_id: '',
						ua_connecting_integration_context: '',
						ua_connecting_integration_condition: '',
					}, {
						setAsURL: true,
						refresh: false
					});
				}, 10 );
			},
		});

		// Add actions to the step buttons
		modal.getElements().modal.container.find( '#connect-integration-modal-sign-up' )
			.on( 'click', ( event ) => {
				// Prevent default behavior
				event.preventDefault();

				// Add loading animation
				event.currentTarget.classList
					.add( 'connect-integration-modal-step__action-anchor--loading' );

				window.location.href = connectApiUrl;
			});

		// Add actions to the step buttons
		modal.getElements().modal.container.find( '#connect-integration-modal-activate-license' )
			.on( 'click', ( event ) => {
				// Prevent default behavior
				event.preventDefault();

				// Add loading animation
				event.currentTarget.classList
					.add( 'connect-integration-modal-step__action-anchor--loading' );

				// Open URL on a new window
				const newWindow = openWindow({
					url:   UncannyAutomator._site.automator.links.manage_license,
					w:     550,
					h:     600,
					onClose: () => {
						location.reload();
					}
				});
			});

		// Check if the current step is the second step or if it's a third-party integration
		if ( options.currentStep == 2 || isThirdPartyIntegration ){
			modal.getElements().modal.container.find( '#connect-integration-modal-connect-account' )
				.on( 'click', ( event ) => {
					// Prevent default behavior
					event.preventDefault();

					// Add loading animation
					event.currentTarget.classList
						.add( 'connect-integration-modal-step__action-anchor--loading' );

					// Open URL on a new window
					const newWindow = openWindow({
						url:   addParametersToURL( integration.settings_url, {
							automator_minimal: 1,
							automator_hide_settings_tabs: 1
						}),
						w:     550,
						h:     600,
						onClose: () => {
							location.reload();
						}
					});
				});
		}            
	}

	handleContinueIntegrationConnection(){
		// Check if the parameter is defined on page load
		if (
			getURLParameter( 'ua_connecting_integration' ) == 1
			&& getURLParameter( 'ua_connecting_integration_context' ) == this.itemType
		){
			// Get the integration code
			const integrationID = getURLParameter( 'ua_connecting_integration_id' );

			// Check if it's defined
			if ( ! isEmpty( integrationID ) ){
				// Get the integration data
				const integration = UncannyAutomator.integrations[ integrationID ];

				// Check if the integration exists
				if ( isDefined( integration ) ){
					// Evaluate if we should show the modal
					if (
						// Integration is an API integration
						isDefined( integration.connected )
					){	
						// Get current step and render content accordingly
						switch ( this.integrationModalCurrentStep( integration, integrationID ) ){
							// Step 1
							case 1:
								// Show the modal to connect the integration
								// 1st step not completed
								this.connectIntegrationModal( integrationID, integration, {
									currentStep: 1
								});

								break;

							// Step 2
							case 2:
								// Show the modal to connect the integration
								this.connectIntegrationModal( integrationID, integration, {
									currentStep: 2
								});

								break;

							// Step 3
							case 3:
								// Get the attributes for the item list
								const itemListAttributes = {}

								// Check if it's inside a conditions block
								const conditionsGroupID = getURLParameter( 'ua_connecting_integration_condition' );
								if ( ! isEmpty( conditionsGroupID ) ) {
									itemListAttributes.conditionsGroupID = conditionsGroupID;
								}

								// Go to the list of items
								new ItemCreatorTaskSelector( this.itemType, integrationID, this.$item, itemListAttributes );

								break;
						}
					}
				}
			}
		}
	}

	createIntegration( integrationID, integration ){
		let container   = $( '<div/>', { 'class': 'item-integration' }),
			logo        = $( '<div/>', { 'class': 'item-integration__logo' }),
			name        = $( '<div/>', { 'class': 'item-integration__name' });

		// Check if it's a Pro only integration
		if ( integration.isProOnly ){
			// Add a class to identify it
			container.addClass( 'item-integration--pro' );

			// Add PRO tag
			container.append( $( `
				<div class="item-integration__badge">${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }</div>
			` ) );
		}

		// Check if it's a Elite only integration
		if ( integration.isEliteOnly ){
			// Add a class to identify it
			container.addClass( 'item-integration--elite' );

			// Add PRO tag
			container.append( $( `
				<div class="item-integration__badge">${ escapeHTML( __( 'Elite', 'uncanny-automator' ) ) }</div>
			` ) );
		}
		
		// Check if the integration is not available
		if ( ! integration.isAvailable ){
			// Add a class to identify it
			container.addClass( 'item-integration--unavailable item-integration--unavailable-requires-pro' );

			// Add tooltip
			container.attr( 
				'uap-tooltip', 
				sprintf( 
					/* translators: 1. Trademarked term */
					escapeHTML( __( 'Requires %1$s', 'uncanny-automator' ) ), 
					'Uncanny Automator Pro'
				) 
			);
		}
		else {
			// Check if it's an API integration
			if ( isDefined( integration.connected ) ){
				// For third-party integrations, skip the automatorplugin.com account requirement
				if ( isThirdParty( integrationID ) ) {
					// Check if the third-party integration is connected
					if ( ! integration.connected ){
						// Add a class to identify it
						container.addClass( 'item-integration--unavailable item-integration--unavailable-integration-not-connected' );

						// Add tooltip
						container.attr( 
							'uap-tooltip', 
							sprintf( 
								/* translators: 1. Trademarked term */
								escapeHTML( __( '%1$s account not connected. Click to connect.', 'uncanny-automator' ) ), 
								integration.name 
							) 
						);

						// Add tag
						container.append( $( `
							<div class="item-integration__badge item-integration__badge--warning">${ escapeHTML( __( 'Not connected', 'uncanny-automator' ) ) }</div>
						` ) );
					}
					else {
						// Add a class to identify it
						container.addClass( 'item-integration--available' );
					}
				}
				// For regular integrations, check if the user connected an automatorplugin.com account
				else if ( UncannyAutomator._site.automator.has_account_connected ){
					// Check if the current action is not available because the
					// user didn't connect an account to it
					if ( ! integration.connected ){
						// Add a class to identify it
						container.addClass( 'item-integration--unavailable item-integration--unavailable-integration-not-connected' );

						// Add tooltip
						container.attr( 
							'uap-tooltip', 
							sprintf( 
								/* translators: 1. Trademarked term */
								escapeHTML( __( '%1$s account not connected. Click to connect.', 'uncanny-automator' ) ), 
								integration.name 
							) 
						);

						// Add tag
						container.append( $( `
							<div class="item-integration__badge item-integration__badge--warning">${ escapeHTML( __( 'Not connected', 'uncanny-automator' ) ) }</div>
						` ) );
					}
					else {
						// Add a class to identify it
						container.addClass( 'item-integration--available' );
					}
				}
				else {
					// Add a class to identify it
					container.addClass( 'item-integration--unavailable item-integration--unavailable-ap-account-not-connected' );

					// Add tooltip
					container.attr( 
						'uap-tooltip', 
						sprintf( 
							/* translators: 1. Trademarked term */
							escapeHTML( __( '%1$s account not connected. Click to connect.', 'uncanny-automator' ) ), 
							integration.name 
						) 
					);

					// Add tag
					container.append( $( `
						<div class="item-integration__badge item-integration__badge--warning">${ escapeHTML( __( 'Not connected', 'uncanny-automator' ) ) }</div>
					` ) );
				} 
			}
			else {
				// Check if we're rendering this in the actions metabox
				// We're checking this because actions need some exceptions
				if ( this.itemType == 'action' ){
					// Check if the integration might have actions that are not available
					// because they require user data
					if (
						// Check if WP is ready
						( ! UncannyAutomatorBackend._site.automator.is_pro_active )
						// Check if the recipe is anonymous
						&& UncannyAutomator._recipe.recipe_type == 'anonymous'
					){
						// Check if it has actions that DON'T require user data
						const actionsThatDontRequireUserData = integration.items.actions.available.filter(( action ) => {
							return action.requires_user == false;
						});

						if ( actionsThatDontRequireUserData.length == 0 ){
							// Add a class to identify it
							container.addClass( 'item-integration--unavailable item-integration--unavailable-requires-user-data' );

							// Add tooltip
							container.attr( 'uap-tooltip', escapeHTML( __( 'All actions need user data', 'uncanny-automator' ) ) );
						}
						else {
							// Add a class to identify it
							container.addClass( 'item-integration--available' );
						}
					}
					else {
						// Add a class to identify it
						container.addClass( 'item-integration--available' );
					}
				}
				else {
					// Add a class to identify it
					container.addClass( 'item-integration--available' );
				}
			}
		}

		// Create the variable where we're going to save the logo
		let $logoImg;

		// Check if it has a svg image
		if ( isDefined( integration.icon_svg ) ){
			$logoImg = $( `<img src="${ integration.icon_svg }" alt="${ integration.name }">` );
		}
		// Check if it has a png image
		else if ( isDefined( integration.icon_32 ) && isDefined( integration.icon_64 ) ){
			$logoImg = $( `<img src="${ integration.icon_32 }" alt="${ integration.name }">` );
		}

		// TO-DO
		// Improve UI/UX
		// Set integration name
		name.text( integration.name );

		// Append
		logo.append( $logoImg );
		container.append( logo );
		container.append( name );

		// Add ID
		container.attr( 'data-integration-id', integrationID );

		// Return jQuery element
		return container;
	}

	/**
	 * Creates the section footer
	 * 
	 * @return {Node} The footer
	 */
	createFooter(){
		// Get the content
		const content = {
			text: ''
		}

		// Check if the current recipe is anonymous
		if ( UncannyAutomator._recipe.recipe_type == 'anonymous' ) {
			content.text = escapeHTML( __( "Don't see your trigger? Try a recipe that runs for logged-in users only.", 'uncanny-automator' ) );
		} else {
			// Create "Learn more" URL
			const learnMoreURL = addParametersUTM( 'https://automatorplugin.com/integrations/', {
				medium:  'select_an_integration',
				content: 'discover_more_integrations'
			});

			content.text = `${ escapeHTML( __( 'Discover more awesome integrations.', 'uncanny-automator' ) ) } <a href="${ learnMoreURL }" target="_blank" id="uap-${ this.itemType }-all-integrations-link">${ escapeHTML( __( 'Learn more', 'uncanny-automator' ) ) } <uo-icon id="external-link"></uo-icon></a>`;
		}

		// Create template
		const $template = document.createElement( 'div' );
		$template.classList.add( 'item-content__description' );
		$template.setAttribute( 'dir', 'auto' );

		// Add content
		$template.innerHTML = content.text;

		// Return template
		return $template;
	}

	filterByNonEmptyIntegrations( itemType ){
		// Check if it's a valid item type
		if ( [ 'trigger', 'action' ].includes( itemType ) ){
			// Get the integrations
			const integrations = $.extend( true, {}, UncannyAutomator.integrations );

			// Iterate each integration
			Object.entries( integrations ).forEach(([ integrationID, integration ]) => {
				// Evaluate if it has the requested items by item type
				let hasItems = false;
				// Also evaluate if it has available items
				let isProOnly = false;
				// Also evaluate if it has available items
				let isEliteOnly = false;
				// Also evaluate if it has available items
				let isAvailable = false;

				// Remove deprecated items
				let items;

				if ( itemType == 'trigger' ){
					// Get items
					items       = filterDeprecatedItems( integration.items.triggers );

					hasItems    = items.hasTriggers;
					isAvailable = items.hasAvailableItems;
					isProOnly   = items.allItemsArePro;
					isEliteOnly   = items.allItemsAreElite;
				}
				else if ( itemType == 'action' ){
					// Get items
					items       = filterDeprecatedItems( integration.items.actions );

					hasItems    = items.hasActions;
					isAvailable = items.hasAvailableItems;
					isProOnly   = items.allItemsArePro;
					isEliteOnly   = items.allItemsAreElite;
				}

				// Check if it has the requested items by item type
				if ( hasItems ){
					// Add integration to the list of integrations
					this.integrations.push( $.extend({}, integration, {
						id:          integrationID,
						isAvailable: isAvailable,
						isProOnly:   isProOnly,
						isEliteOnly: isEliteOnly,
					}) );
				}
			});
		}
	}

	/**
	 * Sorts integrations alphabetically and by connection status
	 * 
	 * @return {undefined}
	 */
	sortIntegrations(){
		// Once sorted alphabetically, show connected integrations first
		const integrations = {
			canUse: [],
			canNotUse: []
		}

		this.integrations.forEach( ( integration ) => {
			// If it's not connected, or not available, it's not ready for use yet
			if ( ( isDefined( integration.connected ) && ! integration.connected ) || ! integration.isAvailable ) {
				integrations.canNotUse.push( integration );
			} else {
				integrations.canUse.push( integration );
			}
		} );

		// Sort alphabetically
		integrations.canUse = sortArrayOfObjectsAlphabetically( integrations.canUse, 'name' );
		integrations.canNotUse = sortArrayOfObjectsAlphabetically( integrations.canNotUse, 'name' );

		// Merge both groups
		this.integrations = [ ...integrations.canUse, ...integrations.canNotUse ];
	}

	/**
	 * Validate what's the current step when connecting a premium integration
	 *
	 * Step 1. Connect ap.com account, or validate license
	 * Step 2. Connect integration account
	 * Step 3. Integration connected
	 *
	 *
	 * @param  {Object}  integration   An object from this.integrations with the integration data
	 * @param  {string}  integrationID The integration ID
	 * @return {Integer}               The current step
	 */
	integrationModalCurrentStep( integration = {}, integrationID = '' ){
		let step;

		// Check if it's a third-party integration
		if ( isThirdParty( integrationID ) ){
			// Then, return the current step
			return integration.connected ? 3 : 2;
		}

		if (
			// Condition #1
			// User has Automator free
			// + ap.com account not connected
			(
				// Check if the user has Automator Free
				! UncannyAutomatorBackend._site.automator.is_pro_active && 
				// Check if the ap.com account is NOT connected
				! UncannyAutomator._site.automator.has_account_connected
			)
			||
			// Condition #2
			// User has Automator Pro
			// + ap.com account not connected
			// + Pro license not valid
			(
				// Check if the user has Automator Pro
				(
					UncannyAutomatorBackend._site.automator.is_pro_active
				)
				&& 
				// Check if the ap.com account is NOT connected
				! UncannyAutomator._site.automator.has_account_connected
				&&
				// Check if the Pro license is not valid
				! UncannyAutomator._site.automator.has_valid_pro_license
			)
		){
			// Then, this is the first step
			// The user has to connect an ap.com account or validate the license
			step = 1;
		}
		else if (
			// Condition #1
			// User has Automator free
			// + ap.com account connected
			(
				// Check if the user has Automator Free
				! UncannyAutomatorBackend._site.automator.is_pro_active && 
				// Check if the ap.com account is connected
				UncannyAutomator._site.automator.has_account_connected
			)
			||
			// Condition #2
			// User has Automator Pro
			// + ap.com account connected OR pro license valid
			(
				// Check if the user has Automator Pro
				(
					UncannyAutomatorBackend._site.automator.is_pro_active
				)
				&& 
				(
					// Check if the ap.com account is connected
					UncannyAutomator._site.automator.has_account_connected
					||
					// Check if the Pro license is valid
					UncannyAutomator._site.automator.has_valid_pro_license
				)
			)
		){
			// Check if the premium integration still doesn't have an account connected
			if ( ! integration.connected ){
				// Then, this is the second step
				// The user has to connect the integration
				step = 2;
			}
			else {
				// Then, this is the third step
				// Everything needed is ready
				step = 3;
			}
		}

		return step;
	}
}

export default ItemCreatorIntegrations;